import '@/themes/augmentations.d.ts';
import { alpha, createTheme, LinkProps } from '@mui/material';
import newtonxColors from './newtonxColors';
import LinkBehavior from '@/components/atoms/LinkBehavior';

const newtonxTheme = createTheme({
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 444,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    primary: {
      light: newtonxColors.blue[400],
      dark: newtonxColors.blue[600],
      main: newtonxColors.blue[500],
      contrastText: '#FFFFFF',
    },
    secondary: {
      light: newtonxColors.lightBlue[300],
      dark: newtonxColors.lightBlue[700],
      main: newtonxColors.lightBlue[500],
      contrastText: newtonxColors.grey[900],
    },
    error: {
      light: newtonxColors.red[400],
      dark: newtonxColors.red[800],
      main: newtonxColors.red[600],
      contrastText: '#FFFFFF',
    },
    warning: {
      light: newtonxColors.orange[400],
      dark: newtonxColors.orange[900],
      main: newtonxColors.orange[800],
      contrastText: '#FFFFFF',
    },
    info: {
      light: newtonxColors.lightBlue[500],
      dark: newtonxColors.lightBlue[900],
      main: newtonxColors.lightBlue[700],
      contrastText: '#FFFFFF',
    },
    success: {
      light: newtonxColors.green[300],
      dark: newtonxColors.green[900],
      main: newtonxColors.green[600],
      contrastText: '#FFFFFF',
    },
    divider: alpha(newtonxColors.grey[900], 0.12),
    text: {
      primary: newtonxColors.grey[900],
      secondary: newtonxColors.grey[800],
      disabled: '#A8AAAB',
    },
    action: {
      active: alpha(newtonxColors.grey[900], 0.56),
      hover: alpha(newtonxColors.grey[900], 0.04),
      selected: alpha(newtonxColors.grey[900], 0.08),
      disabled: alpha(newtonxColors.grey[900], 0.38),
      disabledBackground: alpha(newtonxColors.grey[900], 0.12),
      focus: alpha(newtonxColors.grey[900], 0.12),
    },
  },
  typography: {
    fontFamily: 'DM Sans',
    h1: {
      fontSize: 32,
      fontWeight: 700,
      lineHeight: '37.34px',
      letterSpacing: '-1.5px',
    },
    h2: {
      fontSize: 24,
      fontWeight: 500,
      lineHeight: '28.8px',
      letterSpacing: '-0.5px',
    },
    h3: {
      fontSize: 20,
      fontWeight: 700,
      lineHeight: '23.34px',
      letterSpacing: '-0.5px',
    },
    h4: {
      fontSize: 18,
      fontWeight: 700,
      lineHeight: '22.23px',
      letterSpacing: '-0.25px',
    },
    h5: {
      fontSize: 18,
      fontWeight: 700,
      lineHeight: '24.01px',
    },
    h6: {
      fontSize: 16,
      fontWeight: 700,
      lineHeight: '25.6px',
      letterSpacing: '0.15px',
    },
    body1: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0.15px',
    },
    body2: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '20.02px',
      letterSpacing: '0.17px',
    },
    subtitle1: {
      fontSize: 16,
      fontWeight: 700,
      lineHeight: '24px',
      letterSpacing: '-0.15px',
    },
    subtitle2: {
      fontSize: 14,
      fontWeight: 700,
      lineHeight: '21.98px',
      letterSpacing: '0.1px',
    },
    caption: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '18px',
    },
    overline: {
      fontSize: 12,
      fontWeight: 500,
      lineHeight: '31.92px',
      letterSpacing: '1px',
    },
  },
  spacing: [8, 16, 24, 32, 40, 48, 56, 64, 72, 80, 88, 96],
  components: {
    // Button component
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          textTransform: 'none',
          fontWeight: 700,
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'primary' && {
              borderOpacity: 0.5,
            }),
          ...(ownerState.color === 'secondary' && {
            '&:hover': {
              color: '#FFFFFF', // Custom hover text color for secondary button
            },
          }),
          ...(ownerState.color === 'inherit' && {
            color: newtonxColors.grey[900],
            ...(ownerState.variant === 'contained' && {
              backgroundColor: newtonxColors.grey[200],
            }),
            ...(ownerState.variant === 'outlined' && {
              backgroundColor: '#FFFFFF',
              border: `1px solid ${newtonxColors.grey[500]}`,
            }),
          }),
          boxShadow: 'none',
        }),
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    // Tooltip component
    MuiTooltip: {
      styleOverrides: {
        tooltip: () => ({
          backgroundColor: newtonxColors.grey[900],
          fontSize: 12,
          lineHeight: '14px',
          fontWeight: 500,
        }),
        arrow: {
          color: newtonxColors.grey[900],
        },
      },
    },
    // Avatar component
    MuiAvatar: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          backgroundColor: newtonxColors.grey[400],
          //@ts-ignore
          ...(ownerState.sx?.width === 18 &&
            //@ts-ignore
            ownerState.sx?.height === 18 && {
              fontSize: 8,
              lineHeight: 10,
              '& svg': {
                fontSize: 8,
              },
            }),
          //@ts-ignore
          ...(ownerState.sx?.width === 24 &&
            //@ts-ignore
            ownerState.sx?.height === 24 && {
              fontSize: 10,
              lineHeight: 12,
              '& svg': {
                fontSize: 10,
              },
            }),
          //@ts-ignore
          ...(ownerState.sx?.width === 32 &&
            //@ts-ignore
            ownerState.sx?.height === 32 && {
              fontSize: 14,
              lineHeight: 20,
              letterSpacing: '0.14px',
              '& svg': {
                fontSize: 14,
              },
            }),
          //@ts-ignore
          ...(ownerState.sx?.width === 40 &&
            //@ts-ignore
            ownerState.sx?.height === 40 && {
              fontSize: 14,
              lineHeight: 20,
              letterSpacing: '0.14px',
              '& svg': {
                fontSize: 14,
              },
            }),
        }),
      },
    },
    // Badge component
    MuiBadge: {
      styleOverrides: {
        badge: ({ ownerState }) => ({
          fontSize: 12,
          color: ownerState.color !== 'default' ? '#FFFFFF' : newtonxColors.grey[900],
          lineHeight: '20px',
          fontWeight: 500,
          letterSpacing: '0.14px',
        }),
      },
    },
    // Radio component
    MuiRadio: {
      styleOverrides: {
        root: {
          color: newtonxColors.grey[800],
        },
      },
    },
    // For select and textfield in outlined variant
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& fieldset': {
            color: newtonxColors.grey[800],
            borderColor: alpha('#000000', 0.23), // Default border color
          },
        },
        input: ({ ownerState }) => ({
          ...(ownerState.size === 'medium' && {
            padding: '12.5px 14px',
          }),
        }),
      },
    },
    // For select and textfield in filled variant
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#000000', 0.09),
          '&:hover:not(.Mui-disabled):before': {
            borderBottomWidth: '2px', // Border width on hover for filled input
          },
        },
      },
    },
    // Input label used on forms
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: newtonxColors.grey[800],
          fontSize: 16,
          lineHeight: '24px',
          letterSpacing: '0.15px',
          fontWeight: 400,
        },
        shrink: {
          fontSize: 16,
          letterSpacing: '0.15px',
          fontWeight: 400,
        },
      },
    },
    // ToggleButton component and ToggleGroup
    MuiToggleButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '& > svg': {
            width: ownerState.size === 'small' ? '16px' : '24px',
            height: ownerState.size === 'small' ? '16px' : '24px',
            fill: newtonxColors.grey[800],
          },
          ...((ownerState.color === undefined || ownerState.color === 'standard') && {
            backgroundColor: newtonxColors.grey[200],
            border: 'none',
            color: newtonxColors.grey[800],
            '&.MuiButtonBase-root': {
              borderColor: '#FFFFFF',
            },
            '&.Mui-selected': {
              color: '#FFFFFF', // Text color when selected
              backgroundColor: newtonxColors.grey[900], // Background color when selected
              '& > svg': {
                fill: '#FFFFFF',
              },
            },
            '&:hover': {
              borderColor: '#FFFFFF',
            },
            '&:disabled': {
              color: alpha(newtonxColors.grey[900], 0.38),
              backgroundColor: '#FFFFFF',
              '& > svg': {
                fill: alpha(newtonxColors.grey[900], 0.38),
              },
            },
          }),
          ...(ownerState.color === 'warning' && {
            backgroundColor: newtonxColors.grey[200],
            color: newtonxColors.grey[600],
            border: 'none',
            '&.MuiButtonBase-root': {
              borderColor: '#FFFFFF',
            },
            '&.Mui-selected': {
              color: '#FFFFFF', // Text color when selected
              backgroundColor: newtonxColors.orange[600], // Background color when selected
              '& > svg': {
                fill: '#FFFFFF',
              },
            },
            '&:hover': {
              borderColor: '#FFFFFF',
            },
            '&:disabled': {
              color: alpha(newtonxColors.grey[600], 0.38),
              backgroundColor: '#FFFFFF',
              '& > svg': {
                fill: alpha(newtonxColors.grey[600], 0.38),
              },
            },
          }),
        }),
      },
    },
    // Tabs component
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          lineHeight: '24px',
        },
      },
    },
    // Pagination component
    MuiPagination: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.color === 'secondary' && {
            '& .MuiPaginationItem-page.Mui-selected': {
              color: '#FFFFFF', // Color of selected page number
            },
          }),
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: newtonxColors.grey[800],
          fontSize: 12,
          lineHeight: '18px',
          letterSpacing: '0.4px',
          fontWeight: 400,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: newtonxColors.grey[800],
          fontSize: 16,
          lineHeight: '24px',
          letterSpacing: '0.15px',
          fontWeight: 400,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&:hover:not(.Mui-disabled):after': {
            borderBottomWidth: '2px', // Border width on hover for filled input
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.size === 'medium' && {
            '& .MuiSelect-select': {
              padding: '12.5px 14px',
            },
          }),
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.size === 'medium' && {
            '& input': {
              padding: '12.5px 14px',
            },
            '& label': {
              top: '-4px',
            },
          }),
        }),
      },
    },
    // Enhance Link with react-router link behavior
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
    },
    // Enhance Button link with react-router link behavior
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.size === 'small' && {
            fontSize: '10px',
            lineHeight: '12px',
            height: 'auto',
            paddingTop: '3px',
            paddingBottom: '3px',
          }),
          ...((ownerState.color === 'default' || ownerState.color === undefined) && {
            color: ownerState.variant === 'outlined' ? '#1E1E1E' : '#FFFFFF',
            backgroundColor:
              ownerState.variant === 'outlined'
                ? newtonxColors.action.selected
                : newtonxColors.grey[800],
            borderColor: 'transparent',
          }),
        }),
      },
    },
  },
  gradients: {
    prime: newtonxColors.gradients.prime,
    background: newtonxColors.gradients.background,
    aiComponent: newtonxColors.gradients.aiComponent,
    line: newtonxColors.gradients.line,
    aiIcon: newtonxColors.gradients.aiIcon,
    lightBackground: newtonxColors.gradients.lightBackground,
  },
});

export default newtonxTheme;
